// Connections (Settings → Connections, the /connect ticket page): what a
// workspace is connected to, the catalogue it can connect, and the ticket a
// chat sign-in link carries. Secrets never come back: a key is write-only
// on connect, a token lives in the host-side vault.
//
// The types mirror backend/lattice_api/schemas/connections.py (the operator
// routes in routes/connections.py and routes/connections_setup.py, the
// ticket routes in routes/connections_oauth.py); the readers take a missing
// field as empty. A pasted text or a new key goes up once and never comes
// back: no answer carries it, and no query key holds it.
//
// One query key, ["connections", customerId]: the list answers the switch
// too (409 `connections_disabled` = off, so no second round trip), and
// every mutation invalidates it.

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { proposalsOn } from "@/features/connect/proposalModel";
import { ApiError, apiFetch, errorCode } from "@/lib/api";

export type ConnectionStatus = "ok" | "needs_sign_in" | "failing" | "unchecked" | "paused";

export interface ConnectionAccount {
  kind: "person" | "bot" | "key" | "none";
  subject?: string | null;
  label?: string | null;
}

/** ConnectionView: one connection, never its secret. */
export interface Connection {
  id: string;
  connector: string;
  name: string;
  category?: string;
  purpose?: string;
  /** host / url / base_url of the instance, query and user info stripped. */
  instance?: Record<string, string> | null;
  /** The instance in a few words (a self-managed host), "" for a product with one address. */
  instance_label?: string;
  kind?: "mcp" | "rest";
  auth_kind?: "oauth2" | "api_key" | "none";
  account?: ConnectionAccount | null;
  status: ConnectionStatus | string;
  status_words?: string | null;
  tool_count?: number | null;
  tool_names?: string[];
  agents: string[];
  added_by?: string;
  added_at?: string;
  last_used_at?: string | null;
  needs_sign_in?: boolean;
  /** The sign-in's client regime ("byo": the operator's own app, its id below). */
  client?: string | null;
  client_id?: string | null;
  /** The access a tool asked for that the sign-in has not granted yet (Allow more access). */
  needs_scopes?: string[];
  /** Where the tools came from: catalogue, openapi, docs or edited. */
  tools_source?: string;
  /** The tools are lines the operator may edit (rest-key, openapi-url). */
  tools_editable?: boolean;
  key_set_at?: string;
  /** The REST API names this connection answers to from before it was moved. */
  aliases?: string[];
}

export interface ConnectionList {
  connections: Connection[];
  allowed_hosts: string[];
  /** The customer's agent templates (for scope pickers), never a family agent. */
  agents: string[];
  /** false when the workspace is not on Connections (the list is then empty). */
  enabled: boolean;
  /** true when Add can work out a pasted text ("Paste what you have"); false on an api without it or with it switched off. */
  proposals: boolean;
}

export interface CatalogueEntry {
  id: string;
  name: string;
  category?: string;
  purpose?: string;
  words?: string[];
  /** What to ask for: "none" (one address), "host" (a self-managed product, with its default), "url" / "base_url". */
  instance?: { ask?: string; default?: string | null; placeholder?: string | null; also?: string[] } | null;
  /** In the manifest's order: the first one the instance supports is offered. */
  auth_kinds?: string[];
  docs_url?: string | null;
  /** Where a key is made, and the permissions to tick (the api_key entry). */
  make_at?: string | null;
  needs?: string | null;
  connectable?: boolean; // false: a product with one address that is already connected
  /** A vendor's own server ("registry"; "corvus"-listed when Corvus named it) at its fixed `address`; `featured`: in the empty Add box. */
  source?: "catalogue" | "registry";
  publisher?: string | null;
  address?: string | null;
  listed_by?: "registry" | "corvus" | null;
  logo?: string | null;
  featured?: boolean;
}

export interface SearchOut {
  connected: Connection[];
  catalogue: CatalogueEntry[];
}

/** Which sign-in an instance offers (POST …/probe). */
export interface Capability {
  connector: string;
  offers: string[];
  mcp?: boolean;
  /** How Corvus can register itself for a sign-in there: dynamic registration, or its published client. Neither = no sign-in. */
  dcr?: boolean;
  cimd?: boolean;
  problem?: string | null;
  /** A sign-in works only with the operator's own app there (no registration offered): its id and secret are asked. */
  needs_client?: boolean;
  /** The redirect address that own app must list. */
  callback_url?: string | null;
  scopes?: string[];
  /** An OpenAPI description read at the address (openapi-url). */
  spec?: SpecSummary | null;
  not_approved?: boolean; // the vendor admits only the apps it approved and turned Corvus down: `problem` says so
}

/** Where a key goes: a header or a query parameter by name, a bearer token, or basic auth with the key as the user. */
export interface KeySend {
  in: "header" | "query" | "bearer" | "basic" | string;
  name?: string | null;
}

export interface SpecSummary {
  title?: string | null;
  version?: string | null;
  /** The read tools it compiles to. */
  endpoints?: number;
  /** Changes it describes, left to the generic `request` tool. */
  writes?: number;
  base_url?: string | null;
  key_send?: KeySend | null;
  warnings?: string[];
}

/** How a rest-key / openapi-url key is sent, as the connect body takes it (one of the four). */
export interface SendIn {
  header?: string | null;
  query?: string | null;
  bearer?: boolean;
  basic_user?: boolean;
}

export interface ConnectAuth {
  kind: "oauth2" | "api_key" | "none";
  secret?: string | null;
  /** The operator's own app (a sign-in where Corvus cannot register itself). */
  client_id?: string | null;
  client_secret?: string | null;
  send?: SendIn | null;
}

export interface ConnectBody {
  connector: string;
  instance?: Record<string, string>;
  auth: ConnectAuth;
  agents: string[];
  name?: string | null;
  purpose?: string | null;
}

/** The connection made, or (OAuth) its row waiting for the sign-in and the URL to open. */
export interface ConnectOut {
  connection?: Connection | null;
  authorize_url?: string | null;
  /** The data source made with it (a catalogue data product's data side): its key is the connection's. */
  data_side?: string | null;
}

export interface CheckOut {
  status: string;
  status_words?: string | null;
  tool_count?: number | null;
}

export interface RemovedOut {
  removed: string;
  vendor_revoked?: boolean;
  /** For a key: where to delete it at the product (Lattice cannot). */
  revoke_hint?: string | null;
}

/** What a chat sign-in link is for (POST /api/connections/tickets/peek). */
export interface TicketInfo {
  connector: string;
  connector_name?: string | null;
  name?: string | null;
  instance_label?: string | null;
  agent: string;
  agent_name?: string | null;
  expires_at?: string | null;
}

export const connectionsKey = (customerId: string | null) => ["connections", customerId];

const base = (c: string) => `/api/customers/${encodeURIComponent(c)}/connections`;

const OFF: ConnectionList = { connections: [], allowed_hosts: [], agents: [], enabled: false, proposals: false };

/** Off: the workspace is not on Connections (409 connections_disabled), or an api without the routes (404). */
function isOff(e: unknown): boolean {
  if (!(e instanceof ApiError)) return false;
  if (e.status === 404) return true;
  return e.status === 409 && errorCode(e) === "connections_disabled";
}

async function fetchConnections(customerId: string, signal: AbortSignal | undefined): Promise<ConnectionList> {
  try {
    const list = await apiFetch<Omit<ConnectionList, "enabled" | "proposals"> & { proposals?: boolean }>(base(customerId), { signal });
    return {
      connections: list.connections ?? [],
      allowed_hosts: list.allowed_hosts ?? [],
      agents: list.agents ?? [],
      enabled: true,
      // An older api has no such field: today's Add.
      proposals: list.proposals === true,
    };
  } catch (e) {
    if (isOff(e)) return OFF;
    throw e;
  }
}

export function connectionsListQuery(customerId: string | null) {
  return {
    queryKey: connectionsKey(customerId),
    enabled: customerId !== null && customerId !== "",
    queryFn: ({ signal }: { signal?: AbortSignal }) => fetchConnections(customerId!, signal),
  };
}

/** The page's list: an agent can connect something mid-conversation, so it stays fresh while shown. */
export function useConnections(customerId: string | null) {
  return useQuery({
    ...connectionsListQuery(customerId),
    refetchInterval: (q) => (q.state.data?.enabled ? 15_000 : false),
    staleTime: 5_000,
  });
}

/** true / false once the list answered, undefined until then. Reads the same cache, never polls. */
export function useConnectionsSwitch(customerId: string | null): boolean | undefined {
  const q = useQuery({ ...connectionsListQuery(customerId), staleTime: 60_000 });
  return q.data ? q.data.enabled : undefined;
}

/** Whether Add works a pasted text out ("Paste what you have"): Connections on and the api offering it. Reads the same cache, never polls. */
export function useConnectionProposals(customerId: string | null): boolean {
  const q = useQuery({ ...connectionsListQuery(customerId), staleTime: 60_000 });
  return proposalsOn(q.data);
}

/** The one box: connected rows and catalogue entries for the typed words (pass them debounced; blank = no call). */
export function useConnectionsSearch(customerId: string | null, q: string) {
  const trimmed = q.trim();
  return useQuery({
    queryKey: ["connections", customerId, "search", trimmed],
    enabled: !!customerId && trimmed.length > 0,
    queryFn: ({ signal }) => apiFetch<SearchOut>(`${base(customerId!)}/search?q=${encodeURIComponent(trimmed)}`, { signal }),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

/** Which sign-in the instance offers, once its host or address is typed (blank = no call). */
export function useProbeConnector(customerId: string | null, connector: string, field: string, value: string) {
  const trimmed = value.trim();
  return useQuery({
    queryKey: ["connections", customerId, "probe", connector, field, trimmed],
    enabled: !!customerId && !!connector && !!field && trimmed.length > 0,
    queryFn: ({ signal }) =>
      apiFetch<Capability>(`${base(customerId!)}/probe`, { method: "POST", body: { connector, instance: { [field]: trimmed } }, signal }),
    staleTime: 60_000,
    retry: false,
  });
}

function invalidating<TArgs extends { customerId: string }, TOut>(fn: (args: TArgs) => Promise<TOut>) {
  return () => {
    const qc = useQueryClient();
    // Every connections mutation shows its failure next to its control. A
    // finished one is dropped at once: its variables may hold a key or a paste.
    return useMutation({
      meta: { errorHandled: true },
      gcTime: 0,
      mutationFn: fn,
      onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: connectionsKey(v.customerId) }),
    });
  };
}

export const useConnect = invalidating(({ customerId, body }: { customerId: string; body: ConnectBody }) =>
  apiFetch<ConnectOut>(base(customerId), { method: "POST", body }),
);

export const useScopeConnection = invalidating(({ customerId, id, agents }: { customerId: string; id: string; agents: string[] }) =>
  apiFetch<Connection>(`${base(customerId)}/${encodeURIComponent(id)}/scope`, { method: "PUT", body: { agents } }),
);

/** Rename: its name and what it is for (the id never changes). */
export const useRenameConnection = invalidating(
  ({ customerId, id, name, purpose }: { customerId: string; id: string; name?: string; purpose?: string }) =>
    apiFetch<Connection>(`${base(customerId)}/${encodeURIComponent(id)}`, { method: "PATCH", body: { name, purpose } }),
);

export const useCheckConnection = invalidating(({ customerId, id }: { customerId: string; id: string }) =>
  apiFetch<CheckOut>(`${base(customerId)}/${encodeURIComponent(id)}/check`, { method: "POST" }),
);

/** Sign in again; with no scopes the server adds what a tool was refused for (Allow more access). */
export const useSignInConnection = invalidating(({ customerId, id, scopes }: { customerId: string; id: string; scopes?: string[] }) =>
  apiFetch<{ authorize_url: string }>(`${base(customerId)}/${encodeURIComponent(id)}/sign-in`, {
    method: "POST",
    ...(scopes?.length ? { body: { scopes } } : {}),
  }),
);

export const useRemoveConnection = invalidating(({ customerId, id }: { customerId: string; id: string }) =>
  apiFetch<RemovedOut>(`${base(customerId)}/${encodeURIComponent(id)}`, { method: "DELETE" }),
);

export const useSaveConnectionHosts = invalidating(({ customerId, hosts }: { customerId: string; hosts: string[] }) =>
  apiFetch<Omit<ConnectionList, "enabled">>(`${base(customerId)}/allowed-hosts`, { method: "PUT", body: { hosts } }),
);

// ─── release 2: change key, tools as lines, paste ───

/** A connection's tools as lines (GET …/{id}/tool-lines). */
export interface ToolLines {
  lines: string[];
  /** The tools every such connection keeps (request, describe), named, never lines. */
  fixed: string[];
  /** The row's tools_source: its kind and the address it was read from. */
  source?: ToolsSource | string | null;
  editable: boolean;
  draft?: DraftOut | null;
}

export interface ToolsSource {
  kind?: string;
  url?: string | null;
  title?: string | null;
  version?: string | null;
  read_at?: string | null;
  writes_left?: number | null;
  edited_by?: string | null;
  edited_at?: string | null;
}

export type DraftState = "none" | "running" | "done" | "failed";

/** Tools drafted from a docs page, as lines; nothing is served until they are saved. */
export interface DraftOut {
  state: DraftState;
  source?: "openapi" | "docs" | string | null;
  url?: string | null;
  title?: string | null;
  lines?: string[];
  warnings?: string[];
  /** Why it failed, in words. */
  words?: string | null;
  summary?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
}

/** A line the server refused (422 bad_tool_lines): its number, its text, why. */
export interface ToolLineError {
  line: number;
  text: string;
  words: string;
}

/** What a pasted text holds (POST …/paste). Never the key itself: only whether one was found. */
export interface PasteOut {
  connector?: string | null;
  connector_name?: string | null;
  instance?: Record<string, string>;
  auth_kind?: string | null;
  key_found?: boolean;
  key_send?: KeySend | null;
  found?: string[];
  missing?: string[];
  warnings?: string[];
  /** Not a connection: the page it belongs on (a database: the Data page). */
  elsewhere?: { page: string; words: string } | null;
  candidates?: string[];
  suggested_name?: string | null;
  purpose?: string | null;
}

export interface PasteConnectBody {
  text: string;
  agents: string[];
  name?: string | null;
  purpose?: string | null;
  id?: string | null;
  connector?: string | null;
}

const one = (c: string, id: string) => `${base(c)}/${encodeURIComponent(id)}`;

/** The lines refused by a 422 bad_tool_lines, else []. */
export function toolLineErrors(e: unknown): ToolLineError[] {
  if (!(e instanceof ApiError) || errorCode(e) !== "bad_tool_lines") return [];
  const body = (e.body ?? {}) as { errors?: unknown; detail?: { errors?: unknown } };
  const list = body.detail?.errors ?? body.errors;
  return Array.isArray(list) ? (list as ToolLineError[]) : [];
}

/** A connection's tools as lines; only fetched while its editor is open. */
export function useToolLines(customerId: string | null, id: string | null) {
  return useQuery({
    queryKey: ["connections", customerId, "tool-lines", id],
    enabled: !!customerId && !!id,
    queryFn: ({ signal }) => apiFetch<ToolLines>(`${one(customerId!, id!)}/tool-lines`, { signal }),
    staleTime: Infinity,
    retry: false,
  });
}

const draftKey = (customerId: string | null, id: string | null) => ["connections", customerId, "draft", id];

/** The connection's last draft; read every 3 s while one runs. */
export function useDraftState(customerId: string | null, id: string | null) {
  return useQuery({
    queryKey: draftKey(customerId, id),
    enabled: !!customerId && !!id,
    queryFn: ({ signal }) => apiFetch<DraftOut>(`${one(customerId!, id!)}/tool-lines/draft`, { signal }),
    refetchInterval: (q) => (q.state.data?.state === "running" ? 3_000 : false),
    retry: false,
  });
}

/** Start a draft from a docs page (blank: the address the tools came from). */
export function useDraftTools() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, id, docsUrl }: { customerId: string; id: string; docsUrl?: string | null }) =>
      apiFetch<DraftOut>(`${one(customerId, id)}/tool-lines/draft`, { method: "POST", body: { docs_url: docsUrl?.trim() || null } }),
    onSuccess: (d, v) => {
      qc.setQueryData(draftKey(v.customerId, v.id), d);
      void qc.invalidateQueries({ queryKey: connectionsKey(v.customerId), exact: true });
    },
  });
}

/** Save the box's text as it is: the server reads it line by line, so a refused line's number is the box's own. */
export const useSaveTools = invalidating(({ customerId, id, text }: { customerId: string; id: string; text: string }) =>
  apiFetch<Connection>(`${one(customerId, id)}/tool-lines`, { method: "PUT", body: { lines: text } }),
);

/** Change a key in place: the new one is tried first, and a refused one leaves the old one working. */
export const useRotateKey = invalidating(({ customerId, id, secret }: { customerId: string; id: string; secret: string }) =>
  apiFetch<Connection>(`${one(customerId, id)}/key`, { method: "POST", body: { secret } }),
);

/** A short digest of the pasted text, so the query key never holds the text (or a key in it). */
function digest(text: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) h = Math.imul(h ^ text.charCodeAt(i), 0x01000193);
  return `${text.length}:${(h >>> 0).toString(36)}`;
}

/** What a pasted text holds (pass it debounced; blank = no call). Read once, never retried, dropped when unused. */
export function usePasteRead(customerId: string | null, text: string) {
  const trimmed = text.trim();
  return useQuery({
    queryKey: ["connections", customerId, "paste", digest(trimmed)],
    enabled: !!customerId && trimmed.length > 0,
    queryFn: ({ signal }) => apiFetch<PasteOut>(`${base(customerId!)}/paste`, { method: "POST", body: { text: trimmed }, signal }),
    retry: false,
    gcTime: 0,
    staleTime: Infinity,
  });
}

/** Connect what the text holds: the server reads it again, so the key is never sent apart from it. */
export const usePasteConnect = invalidating(({ customerId, body }: { customerId: string; body: PasteConnectBody }) =>
  apiFetch<ConnectOut>(`${base(customerId)}/paste/connect`, { method: "POST", body }),
);

// ─── the ticket page (no session: the ticket is the proof) ───

export function peekTicket(token: string): Promise<TicketInfo> {
  return apiFetch<TicketInfo>("/api/connections/tickets/peek", { method: "POST", body: { token } });
}

export function startTicket(token: string): Promise<{ authorize_url: string }> {
  return apiFetch<{ authorize_url: string }>("/api/connections/tickets/start", { method: "POST", body: { token } });
}
